import { type Product, formatBRL, formatIsoDate, monthLabel } from '@deizi/shared';
import { Link } from 'react-router';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { formatHm } from '../../lib/numbers';
import { useProductStats } from '../../lib/reports-api';

const pct = (b: number | null) =>
  b === null ? '—' : `${(b / 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

/** "Estatísticas" (board Produto-Visao): sales, real margin, failures and print time. */
export function StatsTab({ product }: { product: Product }) {
  const q = useProductStats(product.id, 3);
  if (q.isPending) return <Skeleton rows={3} height={96} />;
  if (q.isError)
    return (
      <div className="card">
        <ErrorBox
          title="Não foi possível carregar as estatísticas"
          onRetry={() => void q.refetch()}
        />
      </div>
    );
  const s = q.data;
  if (s.units === 0 && s.plates === 0)
    return (
      <div className="card">
        <EmptyState
          title="Nenhuma venda ainda"
          action={
            <Link className="btn" to="/pedidos/novo">
              Registrar um pedido
            </Link>
          }
        >
          Quando o primeiro pedido deste produto for vendido, vendas, margem real e tempo de
          impressão aparecem aqui.
        </EmptyState>
      </div>
    );
  const maxMonth = Math.max(1, ...s.byMonth.map((m) => m.units));
  const failWarn =
    s.failureBps !== null &&
    s.configuredFailureBps !== null &&
    s.failureBps > s.configuredFailureBps;
  const timed = s.variants.filter((v) => v.estSecondsPerUnit && v.realSecondsPerUnit);
  const maxTime = Math.max(
    1,
    ...timed.flatMap((v) => [v.estSecondsPerUnit!, v.realSecondsPerUnit!]),
  );
  const worst = timed
    .map((v) => ({ v, d: (v.realSecondsPerUnit! - v.estSecondsPerUnit!) / v.estSecondsPerUnit! }))
    .toSorted((a, b) => b.d - a.d)[0];
  return (
    <div className="col" style={{ gap: 18 }}>
      <p className="sm muted">
        Últimos 3 meses · {formatIsoDate(s.from).slice(0, 5)} a {formatIsoDate(s.to)} · pedidos
        vendidos
      </p>
      <div className="grid s">
        <div className="card">
          <p className="xs muted">Unidades vendidas</p>
          <p className="fr tab" style={{ fontSize: 24 }}>
            {s.units}
          </p>
          <p className="xs muted">
            {s.variants
              .filter((v) => v.units)
              .map((v) => `${v.name} ${v.units}`)
              .join(' · ')}
          </p>
        </div>
        <div className="card">
          <p className="xs muted">Receita</p>
          <p className="fr tab" style={{ fontSize: 24 }}>
            {formatBRL(s.revenueCentavos)}
          </p>
          <p className="xs muted">
            {s.yearShareBps !== null
              ? `${pct(s.yearShareBps)} da receita de ${s.to.slice(0, 4)}`
              : ''}
          </p>
        </div>
        <div className="card">
          <p className="xs muted">Margem real média</p>
          <p className="fr tab" style={{ fontSize: 24 }}>
            {pct(s.realMarginBps)}
          </p>
          <p className="xs muted">
            {s.estimatedMarginBps !== null ? `estimada era ${pct(s.estimatedMarginBps)}` : ''}
          </p>
        </div>
        <div className="card" style={failWarn ? { borderColor: 'var(--warn)' } : undefined}>
          <p className="xs muted">Taxa de falha</p>
          <p
            className="fr tab"
            style={{ fontSize: 24, color: failWarn ? 'var(--warn)' : undefined }}
          >
            {pct(s.failureBps)}
          </p>
          <p className="xs muted">
            {s.failedPlates} de {s.plates} {s.plates === 1 ? 'placa' : 'placas'}
            {s.configuredFailureBps !== null ? ` · configurado ${pct(s.configuredFailureBps)}` : ''}
          </p>
        </div>
      </div>
      <div className="two">
        <section className="card col" style={{ gap: 12 }}>
          <div className="row between">
            <h2>Unidades por mês</h2>
            <span className="sm muted">
              {monthLabel(s.to.slice(0, 7), { year: false, capital: false })} até hoje
            </span>
          </div>
          <div
            className="bars"
            style={{ height: 140 }}
            role="img"
            aria-label="Unidades vendidas por mês"
          >
            {s.byMonth.map((m) => (
              <div key={m.month} className="b">
                <p className="xs tab" style={{ fontWeight: 600 }}>
                  {m.units}
                </p>
                <span
                  style={{
                    height: `${Math.max(2, (m.units / maxMonth) * 80)}%`,
                    background: 'var(--co)',
                    maxWidth: 48,
                  }}
                />
                <p className="xs muted">
                  {monthLabel(m.month, { year: false, capital: false }).slice(0, 3)}
                </p>
              </div>
            ))}
          </div>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '2fr 60px 1fr 1fr' }}>
              <span>Variação</span>
              <span className="num">Un.</span>
              <span className="num">Receita</span>
              <span className="num">Margem real</span>
            </div>
            {s.variants.map((v) => {
              const low = v.marginBps !== null && v.marginBps < 1500;
              return (
                <div
                  key={v.variantId}
                  className={low ? 'gtr wl' : 'gtr'}
                  style={{ gridTemplateColumns: '2fr 60px 1fr 1fr' }}
                >
                  <span>{v.name}</span>
                  <span className="num">{v.units}</span>
                  <span className="num">{formatBRL(v.revenueCentavos)}</span>
                  <span
                    className="num"
                    style={{
                      color: low ? 'var(--warn)' : undefined,
                      fontWeight: low ? 600 : undefined,
                    }}
                  >
                    {pct(v.marginBps)}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
        <section className="card col" style={{ gap: 12 }}>
          <h2>Tempo real vs. estimado</h2>
          <p className="xs muted">Por unidade, média das impressões concluídas.</p>
          {timed.length === 0 ? (
            <p className="sm muted">Nenhuma impressão concluída no período.</p>
          ) : null}
          {timed.map((v) => {
            const est = v.estSecondsPerUnit!;
            const real = v.realSecondsPerUnit!;
            const delta = Math.round(((real - est) / est) * 100);
            return (
              <div key={v.variantId} className="col" style={{ gap: 4 }}>
                <div className="row between sm">
                  <span style={{ fontWeight: 500 }}>{v.name}</span>
                  <span style={{ color: delta > 5 ? 'var(--warn)' : undefined }}>
                    {formatHm(est)} → <b>{formatHm(real)}</b> · {delta > 0 ? '+' : ''}
                    {delta}%
                  </span>
                </div>
                <span
                  className="hbar"
                  style={{ width: `${(est / maxTime) * 100}%`, background: 'var(--pe)' }}
                />
                <span
                  className="hbar"
                  style={{ width: `${(real / maxTime) * 100}%`, background: 'var(--co)' }}
                />
              </div>
            );
          })}
          <div className="legend">
            <span>
              <i className="sw" style={{ background: 'var(--pe)' }} />
              estimado
            </span>
            <span>
              <i className="sw" style={{ background: 'var(--co)' }} />
              real
            </span>
          </div>
          <p className="xs muted">
            Diferença acima de 5% sugere revisar o tempo no perfil da variação.
          </p>
          {worst && worst.d > 0.05 ? (
            <Link
              className="btn sm"
              to={`/produtos/${product.id}/variacoes`}
              style={{ alignSelf: 'flex-start' }}
            >
              Revisar perfil da {worst.v.name}
            </Link>
          ) : null}
        </section>
      </div>
    </div>
  );
}
